import { For, Show, Suspense, createSignal, createEffect, onMount } from "solid-js";
import { A, createAsync, useSearchParams, type RouteDefinition } from "@solidjs/router";
import { AppShell, PageHeader } from "~/components/layout";
import { Button, Card, CardContent, Badge, Skeleton, EmptyState, Avatar, Input } from "~/components/ui";
import { AddChannelModal } from "~/components/modals";
import { getChannels, toggleChannelMonitoring } from "~/server/bff";
import { formatRelativeTime } from "~/lib/utils";
import { Plus, Search, Video, Target, TrendingUp, Pause, Play, Users } from "lucide-solid";

export const route = {
  preload: () => getChannels(),
} satisfies RouteDefinition;

export default function SourcesPage() {
  if (import.meta.env.DEV) {
    console.info("[debug] render SourcesPage");
  }
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = createSignal("");
  const [showAddModal, setShowAddModal] = createSignal(false);
  const channels = createAsync(() => getChannels(), { deferStream: true });

  // Open modal if ?new=true query param is present
  onMount(() => {
    if (searchParams.new === "true") {
      setShowAddModal(true);
      // Clear the query param
      setSearchParams({ new: undefined });
    }
  });

  const handleAddChannel = () => {
    setShowAddModal(true);
  };

  const filteredChannels = () => {
    const list = channels();
    if (!list) return null;
    if (!searchQuery()) return list;
    const query = searchQuery().toLowerCase();
    return list.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        c.handle.toLowerCase().includes(query) ||
        c.tags.some((t) => t.toLowerCase().includes(query))
    );
  };

  return (
    <AppShell title="Sources">
      <div class="p-6">
        <PageHeader
          title="Sources"
          description="Manage monitored channels and creators"
          actions={
            <Button onClick={handleAddChannel}>
              <Plus class="h-4 w-4 mr-1" />
              Add Channel
            </Button>
          }
        />

        <div class="mb-6">
          <div class="relative max-w-md">
            <Search class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search channels..."
              class="pl-9"
              value={searchQuery()}
              onInput={(e) => setSearchQuery(e.currentTarget.value)}
            />
          </div>
        </div>

        <Suspense fallback={<SourcesSkeleton />}>
          <Show when={filteredChannels()} fallback={<SourcesSkeleton />}>
            {(items) => (
              <Show
                when={items().length > 0}
                fallback={
                  <EmptyState
                    icon={<Users class="h-12 w-12" />}
                    title="No channels found"
                    description={searchQuery() ? "Try different search terms" : "Add your first channel to start monitoring"}
                    actionLabel="Add Channel"
                    onAction={handleAddChannel}
                  />
                }
              >
                <div class="space-y-3">
                  <For each={items()}>
                    {(channel) => (
                      <A href={`/explore/sources/${channel.id}`}>
                        <Card class="hover:bg-accent/50 transition-colors">
                          <CardContent class="p-4">
                            <div class="flex items-center gap-4">
                              <Avatar src={channel.thumbnailUrl} alt={channel.name} size="lg" />
                              <div class="flex-1 min-w-0">
                                <div class="flex items-center gap-2">
                                  <h3 class="font-medium">{channel.name}</h3>
                                  <Show when={channel.isMonitored}>
                                    <Badge variant={channel.monitorFrequency === "priority" ? "default" : "secondary"}>
                                      {channel.monitorFrequency === "priority" ? "Priority" : "Monitored"}
                                    </Badge>
                                  </Show>
                                  <Show when={!channel.isMonitored}>
                                    <Badge variant="outline">Paused</Badge>
                                  </Show>
                                </div>
                                <p class="text-sm text-muted-foreground">{channel.handle}</p>
                                <div class="flex flex-wrap gap-1 mt-2">
                                  <For each={channel.tags}>
                                    {(tag) => (
                                      <Badge variant="outline" class="text-xs">
                                        {tag}
                                      </Badge>
                                    )}
                                  </For>
                                </div>
                              </div>
                              <div class="flex items-center gap-6 text-sm text-muted-foreground">
                                <div class="text-center">
                                  <p class="font-medium text-foreground">{channel.videoCount}</p>
                                  <p class="text-xs">Videos</p>
                                </div>
                                <div class="text-center">
                                  <p class="font-medium text-foreground">{channel.predictionCount}</p>
                                  <p class="text-xs">Predictions</p>
                                </div>
                                <Show when={channel.accuracy !== undefined}>
                                  <div class="text-center">
                                    <p class="font-medium text-foreground">{channel.accuracy}%</p>
                                    <p class="text-xs">Accuracy</p>
                                  </div>
                                </Show>
                                <Show when={channel.lastVideoDate}>
                                  <div class="text-center">
                                    <p class="font-medium text-foreground">{formatRelativeTime(channel.lastVideoDate!)}</p>
                                    <p class="text-xs">Last Video</p>
                                  </div>
                                </Show>
                              </div>
                              <form action={toggleChannelMonitoring} method="post" onClick={(e) => e.stopPropagation()}>
                                <input type="hidden" name="channelId" value={channel.id} />
                                <input type="hidden" name="enabled" value={(!channel.isMonitored).toString()} />
                                <Button
                                  type="submit"
                                  variant="ghost"
                                  size="icon-sm"
                                  title={channel.isMonitored ? "Pause monitoring" : "Resume monitoring"}
                                >
                                  <Show when={channel.isMonitored} fallback={<Play class="h-4 w-4" />}>
                                    <Pause class="h-4 w-4" />
                                  </Show>
                                </Button>
                              </form>
                            </div>
                          </CardContent>
                        </Card>
                      </A>
                    )}
                  </For>
                </div>
              </Show>
            )}
          </Show>
        </Suspense>

        <Show when={showAddModal()}>
          <Suspense>
            <AddChannelModal open={showAddModal()} onOpenChange={setShowAddModal} />
          </Suspense>
        </Show>
      </div>
    </AppShell>
  );
}

function SourcesSkeleton() {
  return (
    <div class="space-y-3">
      <For each={[1, 2, 3, 4]}>
        {() => (
          <Card>
            <CardContent class="p-4">
              <div class="flex items-center gap-4">
                <Skeleton class="h-12 w-12 rounded-full" />
                <div class="flex-1 space-y-2">
                  <Skeleton class="h-5 w-48" />
                  <Skeleton class="h-4 w-32" />
                </div>
                <Skeleton class="h-8 w-24" />
              </div>
            </CardContent>
          </Card>
        )}
      </For>
    </div>
  );
}
